import React, { Component } from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

var CONDITION_LABELS = {
  'NM': 'Near Mint',
  'NMF': 'Near Mint Foil',
  'LP': 'Lightly Played',
  'LPF': 'Lightly Played Foil',
  'MP': 'Moderately Played',
  'MPF': 'Moderately Played Foil',
  'HP': 'Heavily Played',
  'HPF': 'Heavily Played Foil',
};

class PriceHistoryChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      conditionFilter: 'all',
    };
  }

  isDark() {
    return document.documentElement.classList.contains('tw-dark');
  }

  formatDate(dateStr) {
    var d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  formatShortDate(dateStr) {
    var d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  getStats(history) {
    if (!history || history.length === 0) return null;
    var prices = history.map(function(h) { return h.price; });
    var sum = prices.reduce(function(a, b) { return a + b; }, 0);
    var avg = sum / prices.length;
    var low = Math.min.apply(null, prices);
    var high = Math.max.apply(null, prices);
    var totalSales = history.length;

    // Trend: compare avg of first half vs second half
    var trend = null;
    if (history.length >= 4) {
      var mid = Math.floor(history.length / 2);
      // history is sorted newest first, so reverse for chronological
      var chronological = history.slice().reverse();
      var firstHalf = chronological.slice(0, mid);
      var secondHalf = chronological.slice(mid);
      var firstAvg = firstHalf.reduce(function(a, b) { return a + b.price; }, 0) / firstHalf.length;
      var secondAvg = secondHalf.reduce(function(a, b) { return a + b.price; }, 0) / secondHalf.length;
      if (firstAvg > 0) {
        trend = ((secondAvg - firstAvg) / firstAvg) * 100;
      }
    }

    return { avg: avg, low: low, high: high, totalSales: totalSales, trend: trend };
  }

  getUniqueConditions(history) {
    var conditions = {};
    history.forEach(function(h) {
      if (h.condition) {
        conditions[h.condition] = (conditions[h.condition] || 0) + 1;
      }
    });
    return Object.keys(conditions).sort(function(a, b) {
      // Sort by count descending
      return conditions[b] - conditions[a];
    });
  }

  renderSummaryBar(stats) {
    if (!stats) return null;
    var trendColor = stats.trend > 0 ? 'tw-text-green-500' : stats.trend < 0 ? 'tw-text-red-500' : 'tw-text-[#999]';
    var trendArrow = stats.trend > 0 ? '\u2191' : stats.trend < 0 ? '\u2193' : '';

    return (
      <div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-1.5 tw-mb-3">
        <div className="tw-text-xs">
          <span className="tw-text-[#999] dark:tw-text-gray-500 tw-mr-1">Avg</span>
          <span className="tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">${stats.avg.toFixed(2)}</span>
        </div>
        <div className="tw-text-xs">
          <span className="tw-text-[#999] dark:tw-text-gray-500 tw-mr-1">Range</span>
          <span className="tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">${stats.low.toFixed(2)} - ${stats.high.toFixed(2)}</span>
        </div>
        {stats.trend !== null && (
          <div className="tw-text-xs">
            <span className="tw-text-[#999] dark:tw-text-gray-500 tw-mr-1">Trend</span>
            <span className={'tw-font-semibold ' + trendColor}>
              {trendArrow} {Math.abs(stats.trend).toFixed(1)}%
            </span>
          </div>
        )}
        <div className="tw-text-xs">
          <span className="tw-text-[#999] dark:tw-text-gray-500 tw-mr-1">Sales</span>
          <span className="tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">{stats.totalSales}</span>
        </div>
      </div>
    );
  }

  renderEmptyState() {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8 tw-text-center">
        <svg className="tw-w-10 tw-h-10 tw-text-[#ccc] dark:tw-text-[#444] tw-mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 3v18h18" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M7 16l4-4 4 2 5-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-mb-0">No sales data yet</p>
      </div>
    );
  }

  renderSingleSale(sale) {
    var condLabel = sale.condition ? (CONDITION_LABELS[sale.condition] || sale.condition) : null;
    return (
      <div className="tw-flex tw-items-center tw-gap-3 tw-py-3">
        <div className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-flex-shrink-0" style={{ backgroundColor: sale.foil ? '#c9a84c' : '#888' }} />
        <div>
          <span className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">
            ${sale.price.toFixed(2)}
          </span>
          {condLabel && (
            <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-ml-2">{condLabel}</span>
          )}
          <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-ml-2">
            {this.formatDate(sale.date)}
          </span>
        </div>
      </div>
    );
  }

  renderCustomTooltip(props) {
    var active = props.active;
    var payload = props.payload;
    if (!active || !payload || payload.length === 0) return null;

    var data = payload[0].payload;
    if (!data || data.price == null) return null;

    var isDark = document.documentElement.classList.contains('tw-dark');
    return (
      <div
        style={{
          background: isDark ? '#1a1a1a' : '#fff',
          border: '1px solid ' + (isDark ? '#333' : '#e0e0e0'),
          borderRadius: '8px',
          padding: '8px 12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: isDark ? '#fff' : '#1a1a1a' }}>
          ${data.price.toFixed(2)}
          {data.sales > 1 && (
            <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 500, color: '#999' }}>avg</span>
          )}
        </p>
        {data.sales > 1 && (
          <p style={{ margin: '2px 0 0', fontSize: '11px', color: isDark ? '#aaa' : '#666' }}>
            {data.sales} sales
          </p>
        )}
        <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#999' }}>
          {new Date(data.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
      </div>
    );
  }

  render() {
    var history = this.props.history || [];
    var conditionFilter = this.state.conditionFilter;

    if (history.length === 0) {
      return this.renderEmptyState();
    }

    if (history.length === 1) {
      return this.renderSingleSale(history[0]);
    }

    // Get unique conditions for the dropdown
    var uniqueConditions = this.getUniqueConditions(history);
    var hasConditions = uniqueConditions.length > 1;

    // Filter by condition
    var filtered = history;
    if (conditionFilter !== 'all') {
      filtered = history.filter(function(h) {
        return h.condition === conditionFilter;
      });
    }

    if (filtered.length === 0) {
      filtered = history;
    }

    var stats = this.getStats(filtered);

    // Determine foil state for chart line rendering
    var hasFoil = filtered.some(function(h) { return h.foil; });
    var hasRegular = filtered.some(function(h) { return !h.foil; });

    // Sort chronologically for chart (oldest first)
    var sorted = filtered.slice().sort(function(a, b) {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    // Aggregate same-day sales into average price per day
    var self = this;
    var dayBuckets = {};
    sorted.forEach(function(h) {
      var dayKey = new Date(h.date).toISOString().split('T')[0];
      if (!dayBuckets[dayKey]) {
        dayBuckets[dayKey] = { prices: [], foilPrices: [], regularPrices: [], sales: 0, date: h.date };
      }
      dayBuckets[dayKey].prices.push(h.price);
      dayBuckets[dayKey].sales += 1;
      if (h.foil) {
        dayBuckets[dayKey].foilPrices.push(h.price);
      } else {
        dayBuckets[dayKey].regularPrices.push(h.price);
      }
    });

    var dayKeys = Object.keys(dayBuckets).sort();
    var chartData = dayKeys.map(function(key) {
      var bucket = dayBuckets[key];
      var avg = function(arr) {
        if (arr.length === 0) return null;
        return parseFloat((arr.reduce(function(s, v) { return s + v; }, 0) / arr.length).toFixed(2));
      };
      return {
        timestamp: new Date(key + 'T12:00:00').getTime(),
        price: avg(bucket.prices),
        regularPrice: avg(bucket.regularPrices),
        foilPrice: avg(bucket.foilPrices),
        sales: bucket.sales,
        label: self.formatShortDate(bucket.date),
      };
    });

    var dark = this.isDark();
    var axisColor = dark ? '#555' : '#ccc';
    var gridColor = dark ? '#252525' : '#f0f0f0';

    return (
      <div>
        {this.renderSummaryBar(stats)}

        {hasConditions && (
          <div className="tw-mb-3">
            <select
              value={conditionFilter}
              onChange={function(e) {
                var val = e.target.value;
                self.setState({ conditionFilter: val }, function() {
                  if (self.props.onFilterChange) {
                    self.props.onFilterChange(val);
                  }
                });
              }}
              className="tw-text-xs tw-font-medium tw-border tw-border-[#ddd] dark:tw-border-[#444] tw-rounded-lg tw-px-2.5 tw-py-1.5 tw-bg-white dark:tw-bg-[#1a1a1a] tw-text-[#1a1a1a] dark:tw-text-white tw-cursor-pointer tw-outline-none focus:tw-border-brand-gold"
            >
              <option value="all">All Conditions</option>
              {uniqueConditions.map(function(cond) {
                return (
                  <option key={cond} value={cond}>
                    {CONDITION_LABELS[cond] || cond}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <div style={{ width: '100%', height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={function(val) { return self.formatShortDate(val); }}
                tick={{ fontSize: 11, fill: dark ? '#666' : '#999' }}
                stroke={axisColor}
                axisLine={{ stroke: axisColor }}
                tickLine={false}
                minTickGap={40}
              />
              <YAxis
                tickFormatter={function(val) { return '$' + val.toFixed(0); }}
                tick={{ fontSize: 11, fill: dark ? '#666' : '#999' }}
                stroke={axisColor}
                axisLine={false}
                tickLine={false}
                width={45}
                domain={['auto', 'auto']}
              />
              <Tooltip content={function(props) { return self.renderCustomTooltip(props); }} />
              {hasRegular && (
                <Line
                  dataKey="regularPrice"
                  stroke="#888"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#888', stroke: dark ? '#1a1a1a' : '#fff', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#888', stroke: dark ? '#1a1a1a' : '#fff', strokeWidth: 2 }}
                  connectNulls={true}
                  type="monotone"
                  isAnimationActive={false}
                />
              )}
              {hasFoil && (
                <Line
                  dataKey="foilPrice"
                  stroke="#c9a84c"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#c9a84c', stroke: dark ? '#1a1a1a' : '#fff', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#c9a84c', stroke: dark ? '#1a1a1a' : '#fff', strokeWidth: 2 }}
                  connectNulls={true}
                  type="monotone"
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
}

export default PriceHistoryChart;
